import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { findLegalPlaceholder } from "@/lib/legal-guard";
import { sentryCaptureMessage } from "@/lib/sentry";

/**
 * LEGAL-01 runtime guard for the SPA legal routes (/impressum, /terms).
 *
 * The Vite SPA renders client-side, so there is no build-time artifact to assert
 * against (unlike the Astro landing, covered by scripts/check-legal-placeholders.mjs).
 * This component inspects its own rendered textContent and — on a match — replaces
 * the children with an explicit maintenance note and reports once to Sentry.
 *
 * Checked in useLayoutEffect, which runs before the browser paints: the raw
 * placeholder text is never visible to a user, not even for one frame.
 */

/** Feste Texte — kein i18n, damit der Fallback selbst nie von fehlenden Keys abhängt. */
const MAINTENANCE_TITLE = "Diese Seite wird gerade aktualisiert";
const MAINTENANCE_BODY =
  "Die rechtlichen Angaben auf dieser Seite werden derzeit überarbeitet und sind vorübergehend nicht abrufbar.";
const MAINTENANCE_CONTACT = "Bis dahin erreichst du uns jederzeit per E-Mail:";
const SUPPORT_EMAIL = "support@takelead.app";

export function LegalPlaceholderGuard({
  children,
  className,
}: {
  children: ReactNode;
  /** Applied to the wrapper so guarding a route does not change its layout/spacing. */
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const reportedRef = useRef(false);
  const [blocked, setBlocked] = useState(false);

  useLayoutEffect(() => {
    if (blocked) return;
    const token = findLegalPlaceholder(containerRef.current?.textContent ?? "");
    if (!token) return;

    setBlocked(true);
    if (reportedRef.current) return;
    reportedRef.current = true;
    // Route path + placeholder literal only — no user data, no owner data.
    sentryCaptureMessage(
      `legal placeholder rendered on ${window.location.pathname}: ${token}`,
      "error",
    );
  }, [blocked]);

  if (blocked) {
    return (
      <section className="rounded-xl border border-border bg-surface p-6 space-y-3">
        <h2 className="text-base font-semibold text-text">{MAINTENANCE_TITLE}</h2>
        <p className="text-sm text-text-muted leading-relaxed">{MAINTENANCE_BODY}</p>
        <p className="text-sm text-text-muted leading-relaxed">
          {MAINTENANCE_CONTACT}{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-accent hover:underline">
            {SUPPORT_EMAIL}
          </a>
        </p>
      </section>
    );
  }

  return (
    <div ref={containerRef} className={className}>
      {children}
    </div>
  );
}
